import React from 'react';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';

export default function PrivacyPage() {
  return (
    <div>
      <Navigation />
      
      <div className="container py-24 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-[var(--font-display)] tracking-[-0.02em] mb-4">
          Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Last Updated: January 2026
        </p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Ellis Row, a development by Mods Enterprises ("we," "our," or "us"), is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or submit an application for rental or purchase of a unit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-6">Personal Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Submit a rental or purchase application</li>
              <li>Make a reservation deposit or pay an application fee</li>
              <li>Contact us through our contact form</li>
              <li>Sign up for updates or newsletters</li>
            </ul>
            
            <p className="text-muted-foreground leading-relaxed mt-4">
              This information may include: name, email address, phone number, current address, employment information, 
              income details, Social Security number, and payment information.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-6">Automatically Collected Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              When you visit our Site, we automatically collect certain information about your device, including information about 
              your web browser, IP address, time zone, and cookies. We use this information to improve our Site and understand how 
              visitors use it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Process rental and purchase applications</li>
              <li>Conduct background checks, credit checks, and income verification</li>
              <li>Process payments and maintain financial records</li>
              <li>Communicate with you about your application, unit availability, and property updates</li>
              <li>Respond to your inquiries and provide customer service</li>
              <li>Send you marketing communications (with your consent)</li>
              <li>Comply with legal obligations and prevent fraud</li>
              <li>Improve our website and services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Service Providers:</strong> Third-party companies that help us operate our business, such as payment processors 
              (Stripe), background check services, and property management software providers</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, sale, or transfer of company assets</li>
              <li><strong>With Your Consent:</strong> When you give us explicit permission to share your information</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">5. Payment Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              Payment processing is handled by Stripe, a third-party payment processor. We do not store your complete credit card 
              information on our servers. Stripe's use of your information is governed by their Privacy Policy, which can be found at 
              stripe.com/privacy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">6. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or 
              electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">7. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
              unless a longer retention period is required or permitted by law. Application information is typically retained for at 
              least 3 years to comply with fair housing recordkeeping requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">8. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Access to the personal information we hold about you</li>
              <li>Correction of inaccurate or incomplete information</li>
              <li>Deletion of your personal information (subject to legal retention requirements)</li>
              <li>Objection to certain processing activities</li>
              <li>Withdrawal of consent (where processing is based on consent)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise these rights, please contact us at info@liveellisrow.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">9. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our Site is not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-[var(--font-display)] mb-4">11. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Ellis Row (Mods Enterprises)<br />
              Email: info@liveellisrow.com<br />
              Website: liveellisrow.com
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
